import { useEffect, useState } from 'react';
import api from '@/api';
import { Anchor, CheckCircle, XCircle } from 'lucide-react';

export default function ForwarderOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/offers/').then(r => {
      setOffers((r.data || []).filter(o => o.platform === 'freightiq' && o.status === 'pending'));
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/offers/${id}/status`, { status });
    load();
  };

  const StatusBadge = ({ status }) => {
    const map = { pending: 'bg-amber-50 text-amber-700', accepted: 'bg-green-50 text-green-700', rejected: 'bg-red-50 text-red-700' };
    return <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${map[status] || 'bg-slate-50 text-slate-600'}`}>{status}</span>;
  };

  return (
    <div className="space-y-5" data-testid="forwarder-offers-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Freight offers</h1>
        <p className="text-slate-500 text-sm mt-0.5">Pending freight quotes — accept or decline here</p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : offers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Anchor className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No pending freight offers</p>
          <p className="text-slate-400 text-sm mt-1">New quotes will appear when forwarders respond. Past quotes are under Freight history.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Forwarder</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Quote</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Notes</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {offers.map(o => (
                <tr key={o._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4 text-sm font-semibold text-slate-900">{o.supplier_name}</td>
                  <td className="px-5 py-4 text-sm font-bold text-green-700">${o.amount?.toLocaleString()} {o.currency}</td>
                  <td className="px-5 py-4 hidden md:table-cell text-sm text-slate-500 line-clamp-1">{o.notes || '—'}</td>
                  <td className="px-5 py-4"><StatusBadge status={o.status} /></td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      <button onClick={() => updateStatus(o._id, 'accepted')} data-testid={`accept-freight-offer-${o._id}`} className="p-1.5 text-green-600 hover:bg-green-50 rounded">
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button onClick={() => updateStatus(o._id, 'rejected')} data-testid={`reject-freight-offer-${o._id}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
